import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Search, Trash2, Plus, Edit, AlertCircle, Lock
} from 'lucide-react';
import toast from 'react-hot-toast';
import roleService from '../services/roleService';
import permissionService from '../services/permissionService';
import ResponsiveModal, { ResponsiveButton } from './ResponsiveModal';
import EnhancedModal from './EnhancedModal';
import ResponsiveDataTable from './ResponsiveDataTable';
import EnhancedDataTable from './EnhancedDataTableV2';
import CommonLoader from './CommonLoader';
import { CreateButton } from './CommonButtons';

const DataroomRoles = ({ dataroomUuid, isUserContext = false }) => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    permissions: []
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (dataroomUuid) {
      fetchRoles();
      fetchPermissions();
    }
  }, [dataroomUuid, pagination.current_page, searchTerm, activeFilter]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(activeFilter !== '' && { is_active: activeFilter })
      };

      const response = isUserContext
        ? await roleService.dataroom.list(dataroomUuid, params)
        : await roleService.superAdminDataroom.list(dataroomUuid, params);
      
      // Handle response structure
      let rolesData = [];
      if (response) {
        if (response.roles && Array.isArray(response.roles)) {
          rolesData = response.roles;
        } else if (response.data && Array.isArray(response.data)) {
          rolesData = response.data;
        } else if (Array.isArray(response)) {
          rolesData = response;
        }
      }
      
      setRoles(rolesData);
      
      // Handle pagination
      if (response && response.pagination) {
        setPagination({
          current_page: response.pagination.current_page || 1,
          per_page: response.pagination.per_page || 15,
          total: response.pagination.total || rolesData.length,
          last_page: response.pagination.last_page || 1
        });
      } else {
        setPagination({
          current_page: 1,
          per_page: 15,
          total: rolesData.length,
          last_page: 1
        });
      }
    } catch (error) {
      
      if (error.response?.status === 404) {
        toast.error('Roles endpoint not found. Please check API configuration.');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized. Please check your permissions.');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to load roles. Please try again.');
      }
      
      setRoles([]);
      setPagination({
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await permissionService.list();
      
      let permissionsData = [];
      if (response) {
        if (response.permissions && Array.isArray(response.permissions)) {
          permissionsData = response.permissions;
        } else if (response.data && Array.isArray(response.data)) {
          permissionsData = response.data;
        } else if (Array.isArray(response)) {
          permissionsData = response;
        }
      }
      
      setPermissions(permissionsData);
    } catch (error) {
      // Don't show error toast as permissions might not be critical
      setPermissions([]);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Role name is required';
    }
    
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      is_active: true,
      permissions: []
    });
    setFormErrors({});
    setShowCreateModal(true);
  };

  const handleEdit = async (role) => {
    if (!role || !role.id) {
      toast.error('Invalid role data');
      return;
    }
    
    // Use existing data if available
    if (role.name) {
      setSelectedRole(role);
      // Extract permission IDs from the permissions array
      let permissionIds = [];
      if (role.permissions && Array.isArray(role.permissions)) {
        permissionIds = role.permissions.map(perm => {
          if (typeof perm === 'object' && perm !== null) {
            return perm.id || perm.permission_id || perm.uuid;
          }
          return perm;
        }).filter(id => id !== null && id !== undefined);
      }
      setFormData({
        name: role.name,
        description: role.description || '',
        is_active: role.is_active === 1 || role.is_active === true,
        permissions: permissionIds
      });
      setFormErrors({});
      setShowEditModal(true);
      return;
    }
    
    // Otherwise fetch full details
    try {
      setLoading(true);
      const response = isUserContext
        ? await roleService.dataroom.get(dataroomUuid, role.id)
        : await roleService.superAdminDataroom.get(dataroomUuid, role.id);
      
      let roleData = null;
      
      if (response?.data) {
        roleData = response.data.role || response.data.data || response.data;
      } else if (response?.role) {
        roleData = response.role;
      } else if (response) {
        roleData = response;
      }
      
      if (!roleData) {
        toast.error('No role data received');
        return;
      }
      
      setSelectedRole(roleData);
      // Extract permission IDs from the permissions array
      let permissionIds = [];
      if (roleData.permissions && Array.isArray(roleData.permissions)) {
        permissionIds = roleData.permissions.map(perm => {
          if (typeof perm === 'object' && perm !== null) {
            return perm.id || perm.permission_id || perm.uuid;
          }
          return perm;
        }).filter(id => id !== null && id !== undefined);
      }
      setFormData({
        name: roleData.name || '',
        description: roleData.description || '',
        is_active: roleData.is_active === 1 || roleData.is_active === true,
        permissions: permissionIds
      });
      setFormErrors({});
      setShowEditModal(true);
    } catch (error) {
      toast.error('Failed to load role details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (role) => {
    if (!role || !role.id) {
      toast.error('Invalid role data');
      return;
    }
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const data = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        is_active: formData.is_active ? 1 : 0,
        permissions: formData.permissions
      };
      
      await (isUserContext
        ? roleService.dataroom.create(dataroomUuid, data)
        : roleService.superAdminDataroom.create(dataroomUuid, data));
      toast.success('Role created successfully');
      setShowCreateModal(false);
      setFormErrors({});
      fetchRoles();
    } catch (error) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create role');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const data = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        is_active: formData.is_active ? 1 : 0,
        permissions: formData.permissions
      };
      
      await (isUserContext
        ? roleService.dataroom.update(dataroomUuid, selectedRole.id, data)
        : roleService.superAdminDataroom.update(dataroomUuid, selectedRole.id, data));
      toast.success('Role updated successfully');
      setShowEditModal(false);
      setFormErrors({});
      fetchRoles();
    } catch (error) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update role');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedRole || !selectedRole.id) {
      toast.error('No role selected for deletion');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await (isUserContext
        ? roleService.dataroom.delete(dataroomUuid, selectedRole.id)
        : roleService.superAdminDataroom.delete(dataroomUuid, selectedRole.id));
      toast.success('Role deleted successfully');
      setShowDeleteModal(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePermission = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const columns = [
    {
      key: 'name',
      header: 'Role Name',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-gray-900">{row.name || 'N/A'}</span>
        </div>
      )
    },
    {
      key: 'description',
      header: 'Description',
      sortable: false,
      render: (value, row) => (
        <span className="text-sm text-gray-600">
          {row.description || 'No description'}
        </span>
      )
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      render: (value, row) => {
        const isActive = row.is_active === 1 || row.is_active === true;
        return (
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${
            isActive 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      }
    },
    {
      key: 'permissions_count',
      header: 'Permissions',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600">
            {row.permissions?.length || 0} permissions
          </span>
        </div>
      )
    },
    {
      key: 'created_at',
      header: 'Created Date',
      sortable: true,
      render: (value, row) => {
        const dateStr = row.created_at;
        if (!dateStr) return <span className="text-sm text-gray-400">N/A</span>;
        
        try {
          if (typeof dateStr === 'string' && dateStr.includes(' ')) {
            const [date] = dateStr.split(' ');
            return <span className="text-sm text-gray-600">{date}</span>;
          }
          return <span className="text-sm text-gray-600">{new Date(dateStr).toLocaleDateString()}</span>;
        } catch (error) {
          return <span className="text-sm text-gray-600">{dateStr}</span>;
        }
      }
    }
  ];

  const actions = (role) => [
    {
      icon: Edit,
      label: 'Edit',
      onClick: () => handleEdit(role),
      className: 'text-green-600 hover:text-green-800'
    },
    {
      icon: Trash2,
      label: 'Delete',
      onClick: () => handleDelete(role),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  if (loading && roles.length === 0) {
    return <CommonLoader />;
  }

  return (
    <motion.div
      key="roles"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Roles Management</h2>
              <p className="text-sm text-gray-600 mt-1">Manage roles and permissions for this dataroom</p>
            </div>
            <CreateButton
              onClick={handleCreate}
              icon={Shield}
            >
              Create Role
            </CreateButton>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
            <div className="flex flex-1 gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Status Filter */}
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>

          {/* Roles Table */}
          <EnhancedDataTable
            columns={columns}
            data={roles}
            actions={actions}
            loading={loading}
            emptyMessage="No roles found. Click 'Create Role' to add your first role."
            emptyIcon={Shield}
            showColumnControls={true}
            enableColumnResize={true}
            enableDragDrop={true}
            onSort={(key, direction) => {
              const sorted = [...roles].sort((a, b) => {
                let aVal = a[key];
                let bVal = b[key];
                
                if (key === 'created_at') {
                  aVal = aVal ? new Date(aVal).getTime() : 0;
                  bVal = bVal ? new Date(bVal).getTime() : 0;
                } else if (key === 'is_active') {
                  aVal = (aVal === 1 || aVal === true) ? 1 : 0;
                  bVal = (bVal === 1 || bVal === true) ? 1 : 0;
                }
                
                if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                return 0;
              });
              setRoles(sorted);
            }}
          />
          
          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="px-6 py-4 border-t">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                  {pagination.total} roles
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                    disabled={pagination.current_page === 1}
                    className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                    disabled={pagination.current_page === pagination.last_page}
                    className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Role Modal */}
      <EnhancedModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormErrors({});
        }}
        title="Create New Role"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <form onSubmit={handleSubmitCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (formErrors.name) {
                  setFormErrors({ ...formErrors, name: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border ${
                formErrors.name ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="e.g., viewer_role"
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                if (formErrors.description) {
                  setFormErrors({ ...formErrors, description: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border ${
                formErrors.description ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="Brief description of the role"
              rows="2"
            />
            {formErrors.description && (
              <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
            )}
          </div>
          
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Active</span>
            </label>
            <p className="ml-6 text-xs text-gray-500">
              Inactive roles cannot be assigned to users
            </p>
          </div>
          
          {permissions.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions
              </label>
              <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                {permissions.map((permission) => (
                  <label key={permission.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.includes(permission.id)}
                      onChange={() => togglePermission(permission.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{permission.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <ResponsiveButton
              type="submit"
              variant="primary"
              loading={isSubmitting}
            >
              Create Role
            </ResponsiveButton>
            <ResponsiveButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setFormErrors({});
              }}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </form>
      </EnhancedModal>

      {/* Edit Role Modal */}
      <EnhancedModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setFormErrors({});
        }}
        title="Edit Role"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <form onSubmit={handleSubmitEdit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (formErrors.name) {
                  setFormErrors({ ...formErrors, name: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border ${
                formErrors.name ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="e.g., viewer_role"
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                if (formErrors.description) {
                  setFormErrors({ ...formErrors, description: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border ${
                formErrors.description ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="Brief description of the role"
              rows="2"
            />
            {formErrors.description && (
              <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
            )}
          </div>
          
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Active</span>
            </label>
            <p className="ml-6 text-xs text-gray-500">
              Inactive roles cannot be assigned to users
            </p>
          </div>
          
          {permissions.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions
              </label>
              <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                {permissions.map((permission) => (
                  <label key={permission.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.includes(permission.id)}
                      onChange={() => togglePermission(permission.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{permission.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <ResponsiveButton
              type="submit"
              variant="primary"
              loading={isSubmitting}
            >
              Update Role
            </ResponsiveButton>
            <ResponsiveButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowEditModal(false);
                setFormErrors({});
              }}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </form>
      </EnhancedModal>

      {/* Delete Confirmation Modal */}
      <EnhancedModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Role"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              Are you sure you want to delete the role "{selectedRole?.name || 'this role'}"? 
              This action cannot be undone and will remove the role from all assigned users.
            </p>
          </div>
          
          <div className="flex gap-3">
            <ResponsiveButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isSubmitting}
            >
              Delete Role
            </ResponsiveButton>
            <ResponsiveButton
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </div>
      </EnhancedModal>
    </motion.div>
  );
};

export default DataroomRoles;